import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSignOut } from "@/hooks/useSignOut";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Play, Check, Dumbbell, Calendar, Clock, Weight, Hash, LogOut, ChevronRight, Plus, Target, BarChart3 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { ExerciseProgressChart } from "@/components/ExerciseProgressChart";
import { UserProgressDashboard } from "@/components/UserProgressDashboard";
import { useProfile } from "@/hooks/useProfile";

interface UserExercise {
  id: string;
  exercise_id: string;
  scheduled_date: string;
  sets: number;
  reps: number;
  weight: number | null;
  exercises: {
    name: string;
    description: string;
  };
  exercise_logs: ExerciseLog[];
}

interface ExerciseLog {
  id: string;
  performed_reps: number;
  performed_weight: number;
  notes: string;
  logged_at: string;
}

const Dashboard = () => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const { handleSignOut } = useSignOut();
  const { profile, isAdmin } = useProfile();
  const navigate = useNavigate();
  
  const [userExercises, setUserExercises] = useState<UserExercise[]>([]);
  const [loadingExercises, setLoadingExercises] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today's date
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [exerciseLogs, setExerciseLogs] = useState<{[key: string]: {reps: string, weight: string, notes: string}}>({});
  const [personalRecords, setPersonalRecords] = useState<{[key: string]: {weight: number, reps: number}}>({});
  const [exerciseLogsData, setExerciseLogsData] = useState<{[key: string]: ExerciseLog[]}>({});
  const [showProgressDashboard, setShowProgressDashboard] = useState(false);

  useEffect(() => {
    // Redirect to home if not authenticated
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      loadUserExercises();
      loadPersonalRecords();
    }
  }, [user]);

  // Auto-navigate to next day with exercises if current day has none
  useEffect(() => {
    if (userExercises.length > 0 && todaysExercises.length === 0) {
      const today = new Date(selectedDate);
      const nextExerciseDay = userExercises
        .filter(ex => new Date(ex.scheduled_date) >= today)
        .sort((a, b) => new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime())[0];
      
      if (nextExerciseDay && nextExerciseDay.scheduled_date !== selectedDate) {
        setSelectedDate(nextExerciseDay.scheduled_date);
      }
    }
  }, [userExercises, selectedDate]);

  const loadPersonalRecords = async () => {
    if (!user) return;
    
    try {
      // Get all exercise logs for the user to calculate personal records
      const { data, error } = await supabase
        .from('exercise_logs')
        .select(`
          performed_weight,
          performed_reps,
          user_exercises!inner (
            exercise_id,
            user_id
          )
        `)
        .eq('user_exercises.user_id', user.id);

      if (error) {
        console.error('❌ Error loading personal records:', error);
        return;
      }

      // Calculate personal records for each exercise
      const records: {[key: string]: {weight: number, reps: number}} = {};
      
      data?.forEach((log: any) => {
        const exerciseId = log.user_exercises?.exercise_id;
        if (!exerciseId) return;
        
        const currentRecord = records[exerciseId] || { weight: 0, reps: 0 };
        
        // Update weight record if this is higher
        if (log.performed_weight > currentRecord.weight) {
          records[exerciseId] = {
            weight: log.performed_weight,
            reps: log.performed_reps
          };
        }
        // If same weight, update reps if higher
        else if (log.performed_weight === currentRecord.weight && log.performed_reps > currentRecord.reps) {
          records[exerciseId] = {
            weight: log.performed_weight,
            reps: log.performed_reps
          };
        }
      });

      setPersonalRecords(records);
    } catch (error) {
      console.error('💥 Unexpected error loading personal records:', error);
    }
  };

  const loadUserExercises = async () => {
    if (!user) return;
    
    setLoadingExercises(true);
    
    try {
      // Load ALL exercises for the user (not just current week)
      const { data, error } = await supabase
        .from('user_exercises')
        .select(`
          id,
          exercise_id,
          scheduled_date,
          sets,
          reps,
          weight,
          exercises!inner (
            name,
            description
          ),
          exercise_logs (
            id,
            performed_reps,
            performed_weight,
            notes,
            logged_at
          )
        `)
        .eq('user_id', user.id)
        .order('scheduled_date', { ascending: true });

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Error loading exercises:', error);
        }
      } else {
        // Cast the data to match our interface
        const typedData = (data || []).map(item => ({
          ...item,
          exercises: Array.isArray(item.exercises) ? item.exercises[0] : item.exercises
        }));
        
        setUserExercises(typedData);
      }
    } catch (error) {
      console.error('💥 Unexpected error loading exercises:', error);
    } finally {
      setLoadingExercises(false);
    }
  };


  const logExercise = async (userExerciseId: string) => {
    const log = exerciseLogs[userExerciseId];
    if (!log?.reps || !log?.weight) return;


    try {
      const { error } = await supabase
        .from('exercise_logs')
        .insert({
          user_exercise_id: userExerciseId,
          performed_reps: parseInt(log.reps),
          performed_weight: parseFloat(log.weight),
          notes: log.notes || ''
        });

      if (error) {
        console.error('❌ Error logging exercise:', error);
      } else {
        // Clear the form
        setExerciseLogs(prev => ({
          ...prev,
          [userExerciseId]: { reps: '', weight: '', notes: '' }
        }));
        // Reload exercises to show updated logs
        loadUserExercises();
        loadPersonalRecords();
      }
    } catch (error) {
      console.error('💥 Unexpected error logging exercise:', error);
    }
  };

  const updateExerciseLog = (userExerciseId: string, field: string, value: string) => {
    setExerciseLogs(prev => ({
      ...prev,
      [userExerciseId]: {
        reps: prev[userExerciseId]?.reps || '',
        weight: prev[userExerciseId]?.weight || '',
        notes: prev[userExerciseId]?.notes || '',
        [field]: value
      }
    }));
  };

  const getProgressIndicator = (exerciseId: string, currentWeight: number, currentReps: number) => {
    const record = personalRecords[exerciseId];
    if (!record) return null;

    const weightDiff = currentWeight - record.weight;
    const repsDiff = currentReps - record.reps;

    return {
      weight: weightDiff > 0 ? { type: 'increase', value: `+${weightDiff}` } : 
              weightDiff < 0 ? { type: 'decrease', value: `${weightDiff}` } : null,
      reps: repsDiff > 0 ? { type: 'increase', value: `+${repsDiff}` } : 
            repsDiff < 0 ? { type: 'decrease', value: `${repsDiff}` } : null
    };
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Don't render if no user (will redirect)
  if (!user) {
    return null;
  }

  const todaysExercises = userExercises.filter(ex => ex.scheduled_date === selectedDate);
  const completedToday = todaysExercises.filter(ex => ex.exercise_logs.length > 0).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="font-black text-2xl gradient-text">AN</div>
              <Badge variant="secondary">Workout Dashboard</Badge>
            </div>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 py-4 max-w-2xl">

        {/* Date Selection & Stats */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-xl font-bold gradient-text">Workout Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                {completedToday} of {todaysExercises.length} exercises completed today
              </p>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowProgressDashboard(!showProgressDashboard)}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  {showProgressDashboard ? 'Hide Progress' : 'View Progress'}
                </Button>
              )}
            </div>
          </div>

          {/* Exercise Dates Overview */}
          <div className="mt-3 p-3 bg-gray-800 border border-gray-700 rounded-lg">
            <h3 className="font-medium mb-2 text-white text-sm">Scheduled Exercise Dates</h3>
            {loadingExercises ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  // Get unique dates with exercises
                  const exerciseDates = [...new Set(userExercises.map(ex => ex.scheduled_date))]
                    .sort()
                    .slice(0, 10); // Show up to 10 dates
                  
                  if (exerciseDates.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <p className="text-gray-400">No exercises scheduled yet</p>
                      </div>
                    );
                  }
                  
                  return exerciseDates.map((dateStr) => {
                    const date = new Date(dateStr);
                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                    const dayNumber = date.getDate();
                    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
                    
                    const dayExercises = userExercises.filter(ex => ex.scheduled_date === dateStr);
                    const dayCompleted = dayExercises.filter(ex => ex.exercise_logs.length > 0).length;
                    const hasExercises = dayExercises.length > 0;
                    const isCompleted = hasExercises && dayCompleted === dayExercises.length;
                    const isSelected = dateStr === selectedDate;
                    
                    return (
                      <button
                        key={dateStr}
                        onClick={() => setSelectedDate(dateStr)}
                        className={`w-full text-left p-2 rounded transition-all duration-200 ${
                          hasExercises 
                            ? isCompleted
                              ? isSelected
                                ? 'bg-green-600 text-white shadow-lg ring-2 ring-green-400 ring-opacity-50 border-2 border-green-300' 
                                : 'bg-green-700 text-white hover:bg-green-600 cursor-pointer hover:shadow-md border border-green-500'
                              : isSelected
                                ? 'bg-gray-600 text-white shadow-lg ring-2 ring-gray-400 ring-opacity-50 border-2 border-gray-300' 
                                : 'bg-gray-700 text-white hover:bg-gray-600 cursor-pointer hover:shadow-md border border-gray-500'
                            : isSelected
                              ? 'bg-blue-600 text-white shadow-lg ring-2 ring-blue-400 ring-opacity-50 border-2 border-blue-300'
                              : 'bg-gray-700 text-gray-300 cursor-pointer hover:bg-gray-600 border border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="text-xs font-medium">{dayName}</div>
                            <div className="text-xs font-bold">{dayNumber}</div>
                            <div className="text-xs text-gray-300">{monthName}</div>
                          </div>
                          <div className="text-xs font-bold">
                            {dayCompleted}/{dayExercises.length}
                          </div>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            )}
            {/* Color Legend - Professional Design */}
            <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="text-xs font-medium text-gray-300 mb-2">Calendar Legend</div>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                  <span className="text-xs text-gray-400">All completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-600 rounded-full"></div>
                  <span className="text-xs text-gray-400">Some pending</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-700 rounded-full"></div>
                  <span className="text-xs text-gray-400">No exercises</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {todaysExercises.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-medium">
                  {Math.round((completedToday / todaysExercises.length) * 100)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(completedToday / todaysExercises.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Exercises List */}
        {loadingExercises ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading exercises...</span>
          </div>
        ) : todaysExercises.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="py-12 text-center">
              <Dumbbell className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2 text-white">No exercises scheduled</h3>
              <p className="text-gray-300 mb-4">
                No exercises are scheduled for {new Date(selectedDate).toLocaleDateString()}. 
                Try selecting a different date or contact your coach.
              </p>
              {userExercises.length > 0 ? (
                <div className="text-sm text-gray-400">
                  <p>You have {userExercises.length} exercises scheduled this week.</p>
                  <p>Check the week overview above to see when they're scheduled.</p>
                </div>
              ) : (
                <div className="text-sm text-gray-400">
                  <p>No exercises have been assigned to you yet.</p>
                  <p>Contact your coach to get started with your workout program.</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {todaysExercises.map((exercise, index) => {
              const isCompleted = exercise.exercise_logs.length > 0;
              const currentLog = {
                reps: exerciseLogs[exercise.id]?.reps || '',
                weight: exerciseLogs[exercise.id]?.weight || '',
                notes: exerciseLogs[exercise.id]?.notes || ''
              };

              return (
                <div key={exercise.id} className={`${isCompleted ? 'bg-green-900 border-green-700' : 'bg-gray-800 border-gray-700'} border rounded-lg p-3 mb-2`}>
                  {/* Ultra Compact Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isCompleted ? 'bg-green-500 text-white' : 'bg-blue-600 text-white'
                      }`}>
                        {isCompleted ? <Check className="h-3 w-3" /> : index + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">{exercise.exercises.name}</h4>
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                          <span>{exercise.sets}×{exercise.reps}</span>
                          {exercise.weight && <span>• {exercise.weight}kg</span>}
                        </div>
                      </div>
                    </div>
                    <Badge className={`text-xs px-1 py-0.5 ${
                      isCompleted ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'
                    }`}>
                      {isCompleted ? "✅" : "⏳"}
                    </Badge>
                  </div>

                  {/* Previous Logs - Ultra Compact */}
                  {exercise.exercise_logs.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs text-green-400 mb-1">Completed:</div>
                      <div className="space-y-0.5">
                        {exercise.exercise_logs.map((log, logIndex) => (
                          <div key={log.id} className="text-xs text-green-200 bg-green-800 px-2 py-1 rounded">
                            Set {logIndex + 1}: {log.performed_reps}×{log.performed_weight}kg
                            {log.notes && <span className="text-green-300 ml-1">"{log.notes}"</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mobile-Optimized Log Form */}
                  {!isCompleted && (
                    <div className="space-y-2">
                      {/* Horizontal Input Row */}
                      <div className="flex items-center space-x-2">
                        <div className="flex-1">
                          <Input
                            type="number"
                            placeholder="Reps"
                            value={currentLog.reps}
                            onChange={(e) => updateExerciseLog(exercise.id, 'reps', e.target.value)}
                            className={`bg-gray-700 border text-white text-sm text-center py-2 px-2 rounded ${
                              currentLog.reps ? 'border-green-500 bg-green-900/20' : 'border-gray-500'
                            }`}
                          />
                        </div>
                        <div className="flex-1">
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="Weight"
                            value={currentLog.weight}
                            onChange={(e) => updateExerciseLog(exercise.id, 'weight', e.target.value)}
                            className={`bg-gray-700 border text-white text-sm text-center py-2 px-2 rounded ${
                              currentLog.weight ? 'border-green-500 bg-green-900/20' : 'border-gray-500'
                            }`}
                          />
                        </div>
                        <Button 
                          onClick={() => logExercise(exercise.id)}
                          disabled={!currentLog.reps || !currentLog.weight}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Optional Notes */}
                      <Input
                        placeholder="Notes (optional)"
                        value={currentLog.notes}
                        onChange={(e) => updateExerciseLog(exercise.id, 'notes', e.target.value)}
                        className="bg-gray-700 border-gray-500 text-white text-xs py-1 px-2 rounded"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Workout Complete Message */}
        {todaysExercises.length > 0 && completedToday === todaysExercises.length && (
          <Card className="mt-6 bg-green-900 border-green-700">
            <CardContent className="py-6 text-center">
              <div className="text-4xl mb-2">🎉</div>
              <h3 className="text-lg font-semibold text-green-100 mb-2">
                Workout Complete!
              </h3>
              <p className="text-green-200">
                Great job! You've completed all exercises for today. Keep up the excellent work!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Progress Dashboard - Admin Only */}
        {showProgressDashboard && user && isAdmin && (
          <div className="mt-8">
            <UserProgressDashboard userId={user.id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
